import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Moodies } from "@somethings-ui/core/assets";
import { Button, Lightbox } from "@somethings-ui/web";
import {
  DEFAULT_WALL_COLOR,
  MeasuredWallSection,
  WallBasicsCard,
  WallPhotosSection,
  WallPromptsSection,
  WallShelfCard,
  WallShrine,
  WallSongCard,
  buildWallBasicsSections,
  wallGradientCss,
  type WallPhoto,
  type WallShelfCategory,
} from "@somethings-ui/dom";
import { useMenteeWall, type WallBadges, type WallView } from "@/lib/api/walls";
import { WallEmptyState } from "./wall-empty-state";

const MOODIE_SOURCES = Moodies as Record<string, string>;

const isWallEmpty = (wall: WallView) =>
  !wall.background_color &&
  !wall.avatar &&
  wall.stickers.length === 0 &&
  Object.keys(wall.basics).length === 0 &&
  !wall.song &&
  Object.values(wall.shelf).every((items) => !items?.length) &&
  wall.photos.length === 0 &&
  wall.prompts.length === 0;

function moodieSrc(wall: WallView): string {
  const key = wall.avatar?.type === "moodie" ? wall.avatar.moodie : null;
  return (key && MOODIE_SOURCES[key]) || Moodies.yellowSmile;
}

function acknowledgeKey(
  onAcknowledge: ((key: string) => void) | undefined,
  key: string,
) {
  if (!onAcknowledge) return undefined;
  return () => onAcknowledge(key);
}

function acknowledgePrefix(
  onAcknowledge: ((key: string) => void) | undefined,
  prefix: string,
) {
  if (!onAcknowledge) return undefined;
  return (id: string) => onAcknowledge(`${prefix}${id}`);
}

function WallStatus({ message }: { message?: string }) {
  return (
    <div className="flex h-full items-center justify-center">
      {message ? (
        <p className="font-body text-body-3 text-text-tertiary">{message}</p>
      ) : (
        <Loader2 className="size-6 animate-spin text-primary" />
      )}
    </div>
  );
}

function sliceBadges(badges: WallBadges, prefix: string): WallBadges {
  const sliced: WallBadges = {};
  for (const [key, kind] of Object.entries(badges)) {
    if (key.startsWith(prefix)) sliced[key.slice(prefix.length)] = kind;
  }
  return sliced;
}

export function WallPanel({
  wall,
  menteeName,
  badges,
  onAcknowledge,
  onClearAll,
}: {
  wall: WallView;
  menteeName: string;
  badges?: WallBadges;
  onAcknowledge?: (key: string) => void;
  onClearAll?: () => void;
}) {
  const [lightboxPhoto, setLightboxPhoto] = useState<WallPhoto | null>(null);

  const cardBadges = useMemo(() => {
    const all = badges ?? {};
    return {
      basics: sliceBadges(all, "basics."),
      song: all["song"],
      shelf: {
        books: sliceBadges(all, "shelf.books."),
        shows: sliceBadges(all, "shelf.shows."),
        games: sliceBadges(all, "shelf.games."),
      },
      photos: sliceBadges(all, "photos."),
      prompts: sliceBadges(all, "prompts."),
      avatar: all["avatar"],
    };
  }, [badges]);

  const ackShelf = onAcknowledge
    ? (category: WallShelfCategory, externalId: string) =>
        onAcknowledge(`shelf.${category}.${externalId}`)
    : undefined;

  if (isWallEmpty(wall)) return <WallEmptyState />;

  const hidden = wall.hidden_cards;
  const hasBadges = !!onClearAll && Object.keys(badges ?? {}).length > 0;

  return (
    <div className="relative h-full">
      <div
        className="subtle-scrollbar h-full overflow-y-auto"
        style={{ background: wallGradientCss(wall.background_color) }}
      >
        <MeasuredWallSection resetDocumentStyles={false}>
          <div className="flex flex-col gap-8 p-4 pt-7">
            <WallShrine
              name={menteeName}
              backgroundColor={wall.background_color ?? DEFAULT_WALL_COLOR}
              avatar={wall.avatar}
              moodieSrc={moodieSrc(wall)}
              stickers={wall.stickers}
              badge={cardBadges.avatar}
              onAcknowledgeBadge={acknowledgeKey(onAcknowledge, "avatar")}
            />

            <div className="relative z-10 flex flex-col gap-6 pb-6">
              {!hidden.includes("basics") && (
                <WallBasicsCard
                  sections={buildWallBasicsSections(
                    wall.basics,
                    cardBadges.basics,
                  )}
                  readOnly
                  onAcknowledgeBadge={acknowledgePrefix(
                    onAcknowledge,
                    "basics.",
                  )}
                />
              )}
              {!hidden.includes("song") && (
                <WallSongCard
                  song={wall.song}
                  readOnly
                  badge={cardBadges.song}
                  onAcknowledgeBadge={acknowledgeKey(onAcknowledge, "song")}
                />
              )}
              {!hidden.includes("shelf") && (
                <WallShelfCard
                  books={wall.shelf.books}
                  shows={wall.shelf.shows}
                  games={wall.shelf.games}
                  readOnly
                  badges={cardBadges.shelf}
                  onAcknowledgeBadge={ackShelf}
                />
              )}
              {!hidden.includes("photos") && (
                <WallPhotosSection
                  photos={wall.photos}
                  readOnly
                  badges={cardBadges.photos}
                  onAcknowledgeBadge={acknowledgePrefix(
                    onAcknowledge,
                    "photos.",
                  )}
                  onPressPhoto={setLightboxPhoto}
                />
              )}
              {!hidden.includes("prompts") && (
                <WallPromptsSection
                  prompts={wall.prompts}
                  readOnly
                  badges={cardBadges.prompts}
                  onAcknowledgeBadge={acknowledgePrefix(
                    onAcknowledge,
                    "prompts.",
                  )}
                />
              )}
            </div>
          </div>
        </MeasuredWallSection>
        {lightboxPhoto ? (
          <Lightbox
            media={{
              type: "image",
              src: lightboxPhoto.image_url,
              caption: lightboxPhoto.caption ?? null,
            }}
            onClose={() => setLightboxPhoto(null)}
          />
        ) : null}
      </div>
      {hasBadges ? (
        <Button
          type="button"
          variant="primary"
          size="md"
          fullWidth={false}
          onPress={onClearAll}
          className="absolute bottom-4 right-4 z-20"
        >
          Clear all updates
        </Button>
      ) : null}
    </div>
  );
}

export function MenteeWallView({
  mentorshipId,
  menteeName,
  onAcknowledge,
  onClearAll,
}: {
  mentorshipId: string;
  menteeName: string;
  onAcknowledge?: (key: string) => void;
  onClearAll?: () => void;
}) {
  const { data: wall, isPending, isError } = useMenteeWall(mentorshipId);

  if (isPending) return <WallStatus />;

  if (isError || !wall) {
    return <WallStatus message="Couldn't load this teen's profile" />;
  }

  return (
    <WallPanel
      wall={wall}
      menteeName={menteeName}
      badges={onAcknowledge ? wall.badges : undefined}
      onAcknowledge={onAcknowledge}
      onClearAll={onClearAll}
    />
  );
}
